import React, { useCallback, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useMatch, useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import StyledMainMenuButton from 'common/theme/menu/StyledMainMenuButton';
import StyledMainMenuLink from 'common/theme/menu/StyledMainMenuLink';
import StyledMainMenuListItem from 'common/theme/menu/StyledMainMenuListItem';
import StyledMainMenuListItemIcon from 'common/theme/menu/StyledMainMenuListItemIcon';
import StyledMainMenuListItemText from 'common/theme/menu/StyledMainMenuListItemText';
import SidebarPanelContext, { matchesSearchQuery } from 'context/SidebarPanelContext';

const DEFAULT_ON_CLICK = () => {};


function MenuItem({
  exact = false,
  icon,
  iconOnly = false,
  itemKey,
  label,
  notMatchPath = null,
  onClick = DEFAULT_ON_CLICK,
  path
}) {
  // `exact` matches only the item's own path (end match); default matches nested routes too.
  // Needed for parents like OTRIX Play whose path ('/play') is a prefix of its siblings
  // ('/play/routine', '/play/ranking'…) so they don't all stay highlighted at once.
  const match = useMatch(exact ? path : `${path}/*`);
  const excludeMatch = useMatch(`${notMatchPath ?? '/__never__'}/*`);
  const isActive = match !== null && excludeMatch === null;

  const { isMobile, onNavigate, query, registerItem, unregisterItem } = useContext(SidebarPanelContext);
  const navigate = useNavigate();
  // Filtro del buscador del sidebar: oculta el ítem si su label no matchea.
  const visible = matchesSearchQuery(label, query);

  // Registro para el salto con Enter del buscador (orden de render = prioridad).
  useEffect(() => {
    registerItem(itemKey, { label, path });
    return () => unregisterItem(itemKey);
  }, [itemKey, label, path, registerItem, unregisterItem]);

  const handleClick = useCallback(
    (event) => {
      if (onClick) {
        onClick();
      }

      // En el panel MÓVIL se cierra ANTES de navegar. Con el `NavLink` suelto las dos cosas
      // pasaban en el mismo instante: la animación de cierre competía con el render de la
      // pantalla nueva y, si ese render tardaba, el panel se quedaba a medio recoger encima del
      // contenido y parecía trabado. Cerrando primero, el usuario ve una cosa a la vez.
      if (isMobile) {
        event.preventDefault();
        // ENCADENADO, no cronometrado: navegar es lo que ocurre CUANDO el panel avisa que
        // terminó de recogerse. Con un plazo fijo era una carrera —la animación dura ~195 ms— y
        // cualquier carga que se retrasara la perdía, dejando el panel abierto encima.
        onNavigate(() => navigate(path));
        return;
      }

      onNavigate();
    },
    [onClick, onNavigate, isMobile, navigate, path]
  );

  if (!visible) return null;

  return (
    <StyledMainMenuListItem key={itemKey} data-tour={itemKey} onClick={handleClick} disablePadding>
      <StyledMainMenuLink component={NavLink} to={path}>
        <StyledMainMenuButton iconOnly={iconOnly} selected={isActive}>
          {iconOnly ? (
            <Tooltip title={label} placement="right">
              <StyledMainMenuListItemIcon>{icon}</StyledMainMenuListItemIcon>
            </Tooltip>
          ) : (
            <>
              <StyledMainMenuListItemIcon>{icon}</StyledMainMenuListItemIcon>
              <StyledMainMenuListItemText primary={label} />
            </>
          )}
        </StyledMainMenuButton>
      </StyledMainMenuLink>
    </StyledMainMenuListItem>
  );
}

MenuItem.propTypes = {
  exact: PropTypes.bool,
  icon: PropTypes.object.isRequired,
  iconOnly: PropTypes.bool,
  itemKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  notMatchPath: PropTypes.string,
  onClick: PropTypes.func,
  path: PropTypes.string.isRequired
};

export default MenuItem;
